/**
 * statusHistory actions (VE-26730). Public surface: the two fetch requests,
 * the record funnel (dispatched by this module's own watchers, plus the one
 * external-add baseline seed in mainPage), and MARK_TDO_READ_VERIFIED (the
 * read-authorization input, dispatched by the two server-acknowledged TDO
 * loads). Gate/success/failure actions are internal plumbing consumed only by
 * this module's reducer.
 */
import { createAction } from '@reduxjs/toolkit';
import { RecordStatusChangeInput, StatusHistoryRecord } from '../models';

/** Funnel: one dispatched action per history row to persist (best-effort). */
export const RECORD_STATUS_CHANGE = createAction<RecordStatusChangeInput>(
  'STATUS_HISTORY/RECORD_STATUS_CHANGE'
);

/** Tier-1 read: the newest row for a TDO (drives the note pop-up + icon). */
export const FETCH_LATEST_STATUS_ROW = createAction<{
  readonly tdoId: string;
}>('STATUS_HISTORY/FETCH_LATEST_STATUS_ROW');
export const FETCH_LATEST_STATUS_ROW_SUCCESS = createAction<{
  readonly tdoId: string;
  readonly record: StatusHistoryRecord | null;
}>('STATUS_HISTORY/FETCH_LATEST_STATUS_ROW_SUCCESS');
export const FETCH_LATEST_STATUS_ROW_FAILURE = createAction<{
  readonly tdoId: string;
  readonly message: string;
}>('STATUS_HISTORY/FETCH_LATEST_STATUS_ROW_FAILURE');

/** Tier-2 read: one page of the full history (history table, on demand). */
export const FETCH_STATUS_HISTORY = createAction<{
  readonly tdoId: string;
  readonly offset?: number;
}>('STATUS_HISTORY/FETCH_STATUS_HISTORY');
export const FETCH_STATUS_HISTORY_SUCCESS = createAction<{
  readonly tdoId: string;
  readonly records: readonly StatusHistoryRecord[];
  readonly count: number;
}>('STATUS_HISTORY/FETCH_STATUS_HISTORY_SUCCESS');
export const FETCH_STATUS_HISTORY_FAILURE = createAction<{
  readonly tdoId: string;
  readonly message: string;
}>('STATUS_HISTORY/FETCH_STATUS_HISTORY_FAILURE');

/**
 * Authorization funnel input (SECURITY-11): marks a TDO as read-verified for
 * this session. Dispatch ONLY from a code path that has just received a
 * server-acknowledged read of that TDO for this caller (FETCH_TDO_SUCCESS;
 * the document editor's TDO media-info fetch success). Never dispatch from a
 * route init or any path that merely echoes a client-supplied id — the read
 * sagas drop every fetch for an unverified TDO.
 */
export const MARK_TDO_READ_VERIFIED = createAction<{
  readonly tdoId: string;
}>('STATUS_HISTORY/MARK_TDO_READ_VERIFIED');

/** Result of resolving config registry id + published schema (FR-7 gate). */
export const STATUS_HISTORY_GATE_RESOLVED = createAction<{
  readonly enabled: boolean;
  readonly schemaId?: string;
}>('STATUS_HISTORY/GATE_RESOLVED');

export const actionRecordStatusHistory = (payload: RecordStatusChangeInput) =>
  RECORD_STATUS_CHANGE(payload);

export const actionFetchLatestStatusRow = (payload: { tdoId: string }) =>
  FETCH_LATEST_STATUS_ROW(payload);

export const actionMarkTdoReadVerified = (payload: { tdoId: string }) =>
  MARK_TDO_READ_VERIFIED(payload);

export const actionFetchStatusHistory = (payload: {
  tdoId: string;
  offset?: number;
}) => FETCH_STATUS_HISTORY(payload);

/**
 * Hide everything recorded so far for a TDO (VE-26730).
 *
 * Re-tags every live row with `isDeleted: true` and then appends one tombstone.
 * Nothing is removed server-side, but this is NOT a single cheap write — it
 * pages the whole history and rewrites each row.
 */
export const CLEAR_STATUS_HISTORY = createAction<{
  readonly tdoId: string;
}>('STATUS_HISTORY/CLEAR_STATUS_HISTORY');

export const actionClearStatusHistory = (payload: { tdoId: string }) =>
  CLEAR_STATUS_HISTORY(payload);

/**
 * Drops a TDO's loaded page so the next read starts clean.
 *
 * Needed because FETCH_STATUS_HISTORY_SUCCESS merges rather than replaces: a
 * refetch after a clear returns only the newest page re-tagged, and any rows
 * the user had already paged in would keep their stale un-tagged copies and go
 * on rendering. Dispatched by the clear on SUCCESS only — a failed clear must
 * leave the list exactly as it was.
 */
export const RESET_STATUS_HISTORY = createAction<{
  readonly tdoId: string;
}>('STATUS_HISTORY/RESET_STATUS_HISTORY');
